import React from 'react';
import VirtualizedView from './VirtualizedView';

const noop = () => {};

function normalizeLoadResult(result) {
  if (!result || typeof result !== 'object') {
    return { count: 0, update: noop };
  }

  return {
    count: Math.max(0, Number(result.count) || 0),
    update: typeof result.update === 'function' ? result.update : noop,
  };
}

function runDeferred(fn) {
  if (typeof fn !== 'function') {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const run = () => {
      Promise.resolve()
        .then(fn)
        .then(resolve, reject);
    };

    if (typeof queueMicrotask === 'function') {
      queueMicrotask(run);
    } else {
      setTimeout(run, 0);
    }
  });
}

class DataProvider extends React.Component {
  baseOffset = 0;
  view = null;
  mounted = false;

  loading = {
    before: null,
    after: null,
  };

  componentDidMount() {
    this.mounted = true;

    if (this.props.onCreate) {
      this.props.onCreate(this);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  safeForceUpdate = () => {
    if (this.mounted) {
      this.forceUpdate();
    }
  };

  getContentRef = () => {
    return this.view ? this.view.getContentRef() : null;
  };

  isBottomLocked = () => {
    return Boolean(
      this.view &&
      this.view.isBottomLocked &&
      this.view.isBottomLocked()
    );
  };

  onCreateView = view => {
    this.view = view;
  };

  removeBottomLock = () => {
    if (this.view) {
      this.view.removeBottomLock();
    }
  };

  scrollToItem = async (item, options, then) => {
    if (!this.view) return false;
    return this.view.scrollToItem(item, options, then);
  };

  scrollToTop = async () => {
    this.removeBottomLock();

    if (this.view) {
      return this.view.scrollToTop();
    }

    return false;
  };

  scrollToBottom = async () => {
    const items = this.props.items || [];
    const lastItem = items[items.length - 1];

    if (!lastItem || !this.view) {
      return false;
    }

    await this.view.scrollToItem(lastItem, {
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });

    this.view.scrollToBottom();
    return true;
  };

  scrollToTopItem = async () => {
    const items = this.props.items || [];
    const firstItem = items[0];

    this.removeBottomLock();

    if (!firstItem || !this.view) {
      return false;
    }

    return this.view.scrollToItem(firstItem, {
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  };

  scrollToBottomItem = async () => {
    const items = this.props.items || [];
    const lastItem = items[items.length - 1];

    if (!lastItem || !this.view) {
      return false;
    }

    return this.view.scrollToItem(lastItem, {
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
  };

  handleLoadMore = async direction => {
    const { onLoadMore } = this.props;

    if (!onLoadMore) {
      return { count: 0, update: noop };
    }

    if (this.loading[direction]) {
      return this.loading[direction];
    }

    this.loading[direction] = (async () => {
      const result = normalizeLoadResult(await onLoadMore(direction));

      /*
       * When items are prepended, the current content shifts down by `count`.
       * Raising baseOffset keeps the same logical items in view.
       */
      if (direction === 'before' && result.count > 0) {
        this.baseOffset += result.count;
      }

      /*
       * If new items are appended, the user is no longer guaranteed to be
       * hard-locked to the previous bottom unless the viewport decides so.
       */
      if (direction === 'after' && result.count > 0 && this.view) {
        //this.view.removeBottomLock();
      }

      await runDeferred(result.update);
      this.safeForceUpdate();

      return result;
    })();

    try {
      return await this.loading[direction];
    } finally {
      this.loading[direction] = null;
    }
  };

  handleUnload = async (direction, count) => {
    const { onUnload } = this.props;

    if (!onUnload) {
      return { count: 0, update: noop };
    }

    const result = normalizeLoadResult(await onUnload(direction, count));

    if (direction === 'before' && result.count > 0) {
      this.baseOffset = Math.max(0, this.baseOffset - result.count);
    }

    await runDeferred(result.update);
    this.safeForceUpdate();

    return result;
  };

  setScrollPos = async ({ baseOffset = 0, offset = 0, scrollTop = 0 } = {}) => {
    this.baseOffset = Math.max(0, Number(baseOffset) || 0);

    if (!this.view) {
      this.safeForceUpdate();
      return false;
    }

    return this.view.setScrollPos({
      offset,
      scrollTop,
    });
  };

  setScrollTop = scrollTop => {
    if (!this.view || !this.view.setScrollTop) return false;
    return this.view.setScrollTop(scrollTop);
  };

  onScroll = ({ offset, scrollTop }) => {
    if (!this.props.onScroll) return;

    this.props.onScroll({
      baseOffset: this.baseOffset,
      offset,
      scrollTop,
    });
  };

  render() {
    const {
      items = [],
      windowSize,
      getId,
      getSelector,
      renderItems,
    } = this.props;

    return (
      <VirtualizedView
        onScroll={this.props.onScroll ? this.onScroll : undefined}
        onBottomLockChanged={this.props.onBottomLockChanged}
        isBottomAligned={this.props.isBottomAligned}
        onKeyDown={this.props.onKeyDown}
        autoFocus={this.props.autoFocus}
        onCreate={this.onCreateView}
        items={items}
        baseOffset={this.baseOffset}
        windowSize={Math.max(1, Math.ceil(Number(windowSize) || 1))}
        onLoadMore={this.handleLoadMore}
        onUnload={this.handleUnload}
        renderItems={renderItems}
        getSelector={getSelector}
        getId={getId}
        debug={Boolean(this.props.debug)}
      />
    );
  }
}

export { DataProvider };
